/**
 * Phase 6c PR 4 — demo-mode macro plan for `<SeasonGlance />`.
 *
 * Used by `/v2/today?user=ramiro` and friends so the design preview
 * paths render the new SeasonGlance strip + rail without depending on
 * a live `useMacroPlan()` call.
 *
 * Shape mirrors `MacroPlanWeek` from `src/hooks/useMacroPlan.ts`. The
 * dates are anchored on the existing `ramiroToday` / `championshipFinal`
 * fixtures so the SeasonGlance "current week" outline lights up on
 * the same week the rest of the dashboard is treating as "today."
 */

import type { MacroPlanWeek } from '../hooks/useMacroPlan';
import { addDays, demoAnchorMonday, toLocalIso } from './demoAnchor';

const SEASON_ID = 'demo-spring-2026-xc';
const USER_ID = 'demo-ramiro';

function mk(
  n: number,
  mode: string,
  startIso: string,
  meetId: string | null = null,
): MacroPlanWeek {
  return {
    id: `${SEASON_ID}-w${n}`,
    season_id: SEASON_ID,
    user_id: USER_ID,
    week_number: n,
    week_start_date: startIso,
    mode,
    focus: null,
    meet_id: meetId,
    is_ai_proposed: true,
    approved: false,
    approved_by: null,
    approved_at: null,
    created_at: null,
  };
}

/**
 * 7-week demo plan: 2 base + 2 build + 1 competition + 1 taper +
 * 1 recovery. PR-G (DD-6/E5): anchored RELATIVE to the real clock —
 * the current week is always Week 2 · Base, matching
 * `ramiroToday.phaseLabel` — instead of the original static Apr 2026
 * dates, which drifted (the hero's macro-derived week ran ahead of the
 * frozen "This week" grid as real time passed).
 */
export function demoMacroPlan(now: Date = new Date()): MacroPlanWeek[] {
  const week1 = addDays(demoAnchorMonday(now), -7); // today ∈ week 2
  const start = (n: number) => toLocalIso(addDays(week1, (n - 1) * 7));
  return [
    mk(1, 'base', start(1)),
    mk(2, 'base', start(2)),
    mk(3, 'build', start(3), 'demo-meet-dual'),
    mk(4, 'build', start(4)),
    mk(5, 'competition', start(5), 'demo-meet-state'),
    mk(6, 'taper', start(6)),
    mk(7, 'recovery', start(7)),
  ];
}

export const ramiroMacroPlan: MacroPlanWeek[] = demoMacroPlan();

/**
 * Display labels for meet flags — keyed by `week_number`. The string
 * values are the user-facing labels SeasonGlance renders above the
 * cell. Production code should source these from a `useSeasonMeets`
 * hook (deferred to Phase 6b) — here they're a static map for the
 * design preview path.
 */
export const ramiroMacroPlanMeetLabels: Record<number, string> = {
  3: 'Dual mtg',
  5: 'State',
};
